import React, { useEffect, useRef, useState } from "react";
import Drawer from "../shared/Drawer";
import { uploadFile } from "../../services/apiService";
import { formatVoiceDurationLabel } from "./sequenceUtils";

const MAX_DURATION_MS = 60_000;
const MAX_FILE_BYTES = 2 * 1024 * 1024;
const ALLOWED_MIME_TYPES = [
  "audio/webm",
  "audio/mp4",
  "audio/m4a",
  "audio/mpeg",
  "audio/ogg",
  "audio/wav",
];

function pickRecorderMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || "";
}

function normalizeMimeType(mimeType, fileName = "") {
  const raw = (mimeType || "").split(";")[0].trim().toLowerCase();
  if (ALLOWED_MIME_TYPES.includes(raw)) return raw;
  if (raw === "audio/x-m4a" || raw === "audio/aac") return "audio/m4a";
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".webm")) return "audio/webm";
  if (lower.endsWith(".m4a")) return "audio/m4a";
  if (lower.endsWith(".mp4")) return "audio/mp4";
  if (lower.endsWith(".mp3")) return "audio/mpeg";
  if (lower.endsWith(".ogg") || lower.endsWith(".oga")) return "audio/ogg";
  if (lower.endsWith(".wav")) return "audio/wav";
  return raw;
}

function extensionForMime(mimeType) {
  switch (mimeType) {
    case "audio/webm":
      return "webm";
    case "audio/mp4":
      return "mp4";
    case "audio/m4a":
      return "m4a";
    case "audio/mpeg":
      return "mp3";
    case "audio/ogg":
      return "ogg";
    case "audio/wav":
      return "wav";
    default:
      return "webm";
  }
}

function readAudioDurationMs(fileOrBlob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(fileOrBlob);
    const audio = new Audio();
    audio.preload = "metadata";
    audio.onloadedmetadata = () => {
      const durationMs = Math.round((audio.duration || 0) * 1000);
      URL.revokeObjectURL(url);
      if (!Number.isFinite(durationMs) || durationMs <= 0) {
        reject(new Error("Could not read audio duration"));
        return;
      }
      resolve(durationMs);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not load audio preview"));
    };
    audio.src = url;
  });
}

export default function VoiceNoteDrawer({ open, step, onClose, onSave }) {
  const [fallback, setFallback] = useState("");
  const [delay, setDelay] = useState(0);
  const [delayUnit, setDelayUnit] = useState("hours");
  const [voiceFileId, setVoiceFileId] = useState("");
  const [voiceDurationMs, setVoiceDurationMs] = useState(0);
  const [voiceMimeType, setVoiceMimeType] = useState("");
  const [voiceOriginalName, setVoiceOriginalName] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [recordingMs, setRecordingMs] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const startedAtRef = useRef(0);
  const fileInputRef = useRef(null);

  const clearPreview = () => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
  };

  const stopMediaTracks = () => {
    mediaStreamRef.current?.getTracks()?.forEach((track) => track.stop());
    mediaStreamRef.current = null;
  };

  const clearRecordingTimers = () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  };

  const resetVoiceState = () => {
    clearPreview();
    setVoiceFileId("");
    setVoiceDurationMs(0);
    setVoiceMimeType("");
    setVoiceOriginalName("");
    setRecordingMs(0);
    setError("");
  };

  useEffect(() => {
    if (!step) return;
    setFallback(step.fallbackContent || step.fallbackMessage || "");
    setDelay(step.delay ?? 0);
    setDelayUnit(step.delayUnit || "hours");
    setVoiceFileId(step.voiceFileId || "");
    setVoiceDurationMs(step.voiceDurationMs || 0);
    setVoiceMimeType(step.voiceMimeType || "");
    setVoiceOriginalName(step.voiceOriginalName || "");
    setError("");
    clearPreview();
  }, [step]);

  useEffect(() => () => {
    clearRecordingTimers();
    stopMediaTracks();
    clearPreview();
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const ingestAudioBlob = async ({ blob, mimeType, originalName, durationHintMs }) => {
    const normalizedMime = normalizeMimeType(mimeType || blob.type, originalName);
    if (!ALLOWED_MIME_TYPES.includes(normalizedMime)) {
      throw new Error("Unsupported audio type. Use webm, mp4, m4a, mp3, ogg, or wav.");
    }
    if (blob.size > MAX_FILE_BYTES) {
      throw new Error("Audio must be 2MB or smaller.");
    }
    let durationMs = durationHintMs || 0;
    if (!durationMs) {
      durationMs = await readAudioDurationMs(blob);
    }
    if (durationMs > MAX_DURATION_MS) {
      throw new Error("Voice notes are limited to 60 seconds.");
    }
    const fileName =
      originalName
      || `voice-note-${Date.now()}.${extensionForMime(normalizedMime)}`;
    const file = new File([blob], fileName, { type: normalizedMime });
    const description = JSON.stringify({
      purpose: "sequence_voice_note",
      durationMs,
    });
    setUploading(true);
    setError("");
    try {
      const { data, error: uploadError } = await uploadFile(
        file,
        () => {},
        () => {},
        () => {},
        false,
        fileName,
        description,
      );
      if (uploadError || !data?.id) {
        throw new Error(uploadError?.message || "Upload failed");
      }
      clearPreview();
      setPreviewUrl(URL.createObjectURL(blob));
      setVoiceFileId(data.id);
      setVoiceDurationMs(durationMs);
      setVoiceMimeType(normalizedMime);
      setVoiceOriginalName(data.originalName || fileName);
    } finally {
      setUploading(false);
    }
  };

  const stopRecording = () => {
    clearRecordingTimers();
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    setIsRecording(false);
  };

  const startRecording = async () => {
    setError("");
    if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setError("Recording is not supported in this browser. Upload a file instead.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const mimeType = pickRecorderMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      startedAtRef.current = Date.now();
      setRecordingMs(0);
      setIsRecording(true);
      recorder.ondataavailable = (event) => {
        if (event.data?.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = async () => {
        stopMediaTracks();
        const recordedMs = Math.min(MAX_DURATION_MS, Date.now() - startedAtRef.current);
        const blobType = recorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: blobType });
        chunksRef.current = [];
        try {
          await ingestAudioBlob({
            blob,
            mimeType: blobType,
            durationHintMs: recordedMs,
          });
        } catch (err) {
          setError(err?.message || "Failed to save recording");
          resetVoiceState();
        }
      };
      recorder.start(250);
      tickRef.current = setInterval(() => {
        const elapsed = Date.now() - startedAtRef.current;
        setRecordingMs(elapsed);
        if (elapsed >= MAX_DURATION_MS) {
          stopRecording();
        }
      }, 200);
    } catch (err) {
      stopMediaTracks();
      setIsRecording(false);
      setError(err?.message || "Microphone access denied");
    }
  };

  const handleFilePick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setError("");
    try {
      await ingestAudioBlob({
        blob: file,
        mimeType: file.type,
        originalName: file.name,
      });
    } catch (err) {
      setError(err?.message || "Failed to upload audio");
      resetVoiceState();
    }
  };

  const canSave = Boolean(voiceFileId) && voiceDurationMs > 0 && !uploading && !isRecording;

  const handleSave = () => {
    if (!canSave) {
      setError("Record or upload a voice note before saving.");
      return;
    }
    onSave?.({
      ...step,
      type: "VOICE_NOTE",
      voiceFileId,
      voiceDurationMs,
      voiceMimeType,
      voiceOriginalName,
      fallbackContent: fallback,
      fallbackMessage: fallback,
      delay: Number(delay) || 0,
      delayUnit,
      content: "[Voice note]",
      label: `Voice note · ${formatVoiceDurationLabel(voiceDurationMs)}`,
    });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Voice note"
      widthClass="max-w-lg"
      footer={
        <>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border text-sm">
            Dismiss
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="px-4 py-2 rounded-lg bg-blue-500 text-white text-sm disabled:opacity-40"
          >
            Save
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-xl border border-gray-200 p-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-medium text-gray-800">Audio</div>
              <div className="text-xs text-gray-500">Max 60s · 2MB · webm/mp4/m4a/mp3/ogg/wav</div>
            </div>
            {voiceDurationMs || isRecording ? (
              <span className="text-sm font-medium tabular-nums text-gray-700">
                {formatVoiceDurationLabel(isRecording ? recordingMs : voiceDurationMs)}
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {!isRecording ? (
              <button
                type="button"
                onClick={startRecording}
                disabled={uploading}
                className="px-3 py-2 rounded-lg bg-blue-500 text-white text-sm disabled:opacity-40"
              >
                Record
              </button>
            ) : (
              <button
                type="button"
                onClick={stopRecording}
                className="px-3 py-2 rounded-lg bg-red-500 text-white text-sm"
              >
                Stop
              </button>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || isRecording}
              className="px-3 py-2 rounded-lg border text-sm disabled:opacity-40"
            >
              Upload file
            </button>
            {voiceFileId ? (
              <button
                type="button"
                onClick={resetVoiceState}
                disabled={uploading || isRecording}
                className="px-3 py-2 rounded-lg border text-sm text-red-600 disabled:opacity-40"
              >
                Clear
              </button>
            ) : null}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/webm,audio/mp4,audio/m4a,audio/mpeg,audio/ogg,audio/wav,.webm,.m4a,.mp4,.mp3,.ogg,.wav"
            className="hidden"
            onChange={handleFilePick}
          />
          {uploading ? <p className="text-xs text-gray-500">Uploading…</p> : null}
          {previewUrl ? (
            <audio controls src={previewUrl} className="w-full" />
          ) : voiceFileId ? (
            <p className="text-xs text-gray-500 truncate">
              Saved: {voiceOriginalName || voiceFileId}
            </p>
          ) : (
            <p className="text-xs text-gray-500">No voice note yet.</p>
          )}
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Fallback message</label>
          <textarea
            value={fallback}
            onChange={(e) => setFallback(e.target.value)}
            rows={3}
            className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm"
            placeholder="Sent as text if LinkedIn voice send fails (optional)"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-gray-700">Delay</label>
            <input
              type="number"
              min={0}
              value={delay}
              onChange={(e) => setDelay(e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Unit</label>
            <select
              value={delayUnit}
              onChange={(e) => setDelayUnit(e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="hours">Hours</option>
              <option value="days">Days</option>
              <option value="weeks">Weeks</option>
            </select>
          </div>
        </div>
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
      </div>
    </Drawer>
  );
}
